import React, {forwardRef, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {FocusTrap} from 'focus-trap-react'
import {Box, Flex, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import loadable from '@loadable/component'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'
import {resolveGDIStyles, GDI_TEXT_BLOCK_FIELD_MAP} from '../optimizely/gdi/utils/gdi-styles'

import useQuickView from './hooks/use-quick-view'
import useQuickViewCartEdit from './hooks/use-quick-view-cart-edit'
import {hasAllValidVariationSelections} from '../../utils/variation-selection'
import {useSearchModal} from '../../contexts/search-modal-context'
import QuickViewMobileHeader from './partials/quick-view-mobile-header'
import QuickViewVariations from './partials/quick-view-variations'
import QuickViewActions from './partials/quick-view-actions'
import QuickViewFullProductLink from './partials/quick-view-full-product-link'
import QuickViewCloseButton from './partials/quick-view-close-button'
import QuickViewSizeChart from './partials/quick-view-size-chart'
import {resolveSizeGuideBlocks} from './partials/size-guide-utils'

import * as customStyles from './styles.module.css'

const ShopSimilarModal = loadable(() => import('../shop-similar-modal'), {ssr: false})

const QuickView = forwardRef(
    (
        {
            productId,
            showImageGallery = false,
            imageSize = 'md',
            isProductPartOfSet = false,
            isProductPartOfBundle = false,
            setIsQuickViewOpen,
            isNotifyMeModalStateOpen,
            isFavourite = false,
            wishlistCount,
            setColorValue,
            setProductId,
            onQuickViewClose,
            isWishlistPage = false,
            wishlistProductId,
            disableFocusTrap = false,
            hasLengthVariant = false,
            searchHitData = null,
            onVariantSelect = null,
            onProductChange = null,
            onLocalSelectionsChange = null,
            themeVariant = undefined,
            forceMobileLayout = false,
            enableAutoScroll = true,
            isQuickAdd = false,
            onShopSimilarStateChange = null,
            showBackButton = false,
            onBack = null,
            cartEditMode = false,
            cartEditItem = null,
            onCartEditUpdate = null
        },
        ref
    ) => {
        const {closeSearchModal} = useSearchModal() || {}

        const {
            isDesktop,
            product,
            variationParams,
            variationAttributes,
            variant,
            priceData,
            showSkeleton,
            showOptionsMessage,
            missingAttributeMessages,
            getAttributeLabel,
            isNotifyMe,
            isFewItemsLeft,
            isNotifyMeLoading,
            notifyMeError,
            isNotifyMeModalOpen,
            notifyMeSuccessContent,
            disableAddToCart,
            cartInventoryMessage,
            handleNotifyMe,
            handleNotifyMeCloseModal,
            handleCartItem,
            handleSwatchChange,
            getInitialSlideIndex,
            notifyMeIcon,
            optimizelyData,
            isWishlistEnabled,
            handleWrapperClick,
            optionsAttempt,
            triggerOptionsAttempt,
            hasUnselectedVariations,
            setShowOptionsMessage,
            quantity,
            isVariationDataLoading,
            isAvailabilityResolved,
            isAvailabilityPending
        } = useQuickView({
            productId,
            isProductPartOfSet,
            isProductPartOfBundle,
            setIsQuickViewOpen,
            isNotifyMeModalStateOpen,
            setColorValue,
            setProductId,
            isWishlistPage,
            wishlistProductId,
            searchHitData,
            onVariantSelect,
            onProductChange,
            onLocalSelectionsChange,
            onQuickViewClose: () => {
                onQuickViewClose?.()
                closeSearchModal?.()
            },
            forceMobileLayout
        })

        const intl = useIntl()
        const config = getConfig()
        const {site} = useMultiSite()
        const siteId = site?.id
        const styles = useMultiStyleConfig('QuickView', {
            isDesktop,
            variant: themeVariant,
            cartEditMode
        })

        // Keep variation params stable for images while product data is loading
        // This prevents the image carousel from going empty during color transitions
        const stableImageParamsRef = useRef(variationParams)
        if (!isVariationDataLoading) {
            stableImageParamsRef.current = variationParams
        }

        const [isSizeChartOpen, setIsSizeChartOpen] = useState(false)
        const [isShopSimilarOpen, setIsShopSimilarOpen] = useState(false)
        const hasSizeChartContent = useMemo(
            () =>
                resolveSizeGuideBlocks(
                    optimizelyData?.item?.SizeGuides,
                    product?.c_sizeChartWeb
                ).length > 0,
            [optimizelyData, product?.c_sizeChartWeb]
        )
        const isQuickViewMode = themeVariant === 'QuickView'
        const allVariationsSelected = hasAllValidVariationSelections(variationAttributes)

        const shouldShowShopSimilar =
            getSiteEnvVarValue(config.app, 'VICKY_ENABLED', siteId) === 'true' &&
            !isNotifyMe &&
            ((variant &&
                Object.prototype.hasOwnProperty.call(variant, 'orderable') &&
                !variant?.orderable) ||
                (allVariationsSelected && !variant))

        const notifyMeButtonExpanded =
            optimizelyData?.item?.NotifyMeButtonText?.[0]?.ContentLink?.Expanded
        const notifyMeButtonText = notifyMeButtonExpanded?.TextForWeb
            ? notifyMeButtonExpanded.TextForWeb.replace(/<[^>]*>/g, '').trim()
            : ''
        const {styles: notifyMeButtonResolvedStyles} = resolveGDIStyles(
            notifyMeButtonExpanded?.GDITextBlockStylesPerViewType,
            GDI_TEXT_BLOCK_FIELD_MAP
        )
        const notifyMeButtonSx =
            Object.keys(notifyMeButtonResolvedStyles).length > 0
                ? notifyMeButtonResolvedStyles
                : null

        const handleShopSimilarOpen = () => {
            setIsShopSimilarOpen(true)
            onShopSimilarStateChange?.(true)
        }
        const handleShopSimilarClose = () => {
            setIsShopSimilarOpen(false)
            onShopSimilarStateChange?.(false)
        }

        const cartEdit = useQuickViewCartEdit({
            product,
            variant,
            quantity,
            selectedItem: cartEditItem,
            setIsQuickViewOpen,
            hasUnselectedVariations,
            setShowOptionsMessage,
            onMissingVariations: triggerOptionsAttempt,
            onUpdateSuccess: onCartEditUpdate,
            intl
        })

        const handleFullProductLinkClick = () => {
            setIsQuickViewOpen?.(false)
            onQuickViewClose?.()
            closeSearchModal?.()
        }

        const content = (
            <Box
                {...styles.root}
                onClick={handleWrapperClick}
                className={customStyles.quickViewRoot}
            >
                <Flex {...styles.container} data-testid="quick-view-container" ref={ref}>
                    {isSizeChartOpen && !isDesktop ? (
                        <QuickViewSizeChart
                            optimizelyContent={optimizelyData}
                            sizeChartId={product?.c_sizeChartWeb}
                            onBack={() => setIsSizeChartOpen(false)}
                        />
                    ) : (
                        <Flex {...styles.layout}>
                            <QuickViewMobileHeader
                                styles={styles}
                                showSkeleton={showSkeleton}
                                showImageGallery={showImageGallery}
                                imageSize={imageSize}
                                product={product}
                                variationParams={stableImageParamsRef.current}
                                isProductPartOfSet={isProductPartOfSet}
                                isProductPartOfBundle={isProductPartOfBundle}
                                isWishlistEnabled={isWishlistEnabled}
                                isFavourite={isFavourite}
                                wishlistCount={wishlistCount}
                                priceData={priceData}
                                searchHitData={searchHitData}
                                forceMobileLayout={forceMobileLayout}
                            />
                            <QuickViewVariations
                                styles={styles}
                                customStyles={customStyles}
                                showSkeleton={showSkeleton}
                                hasLengthVariant={hasLengthVariant}
                                variationAttributes={variationAttributes}
                                isDesktop={isDesktop}
                                showOptionsMessage={showOptionsMessage}
                                missingAttributeMessages={missingAttributeMessages}
                                getAttributeLabel={getAttributeLabel}
                                isNotifyMe={isNotifyMe}
                                isFewItemsLeft={isFewItemsLeft}
                                variant={variant}
                                getInitialSlideIndex={getInitialSlideIndex}
                                handleSwatchChange={handleSwatchChange}
                                notifyMeIcon={notifyMeIcon}
                                product={product}
                                onOpenSizeChart={() => setIsSizeChartOpen(true)}
                                hasSizeChartContent={hasSizeChartContent}
                                optionsAttempt={optionsAttempt}
                                enableAutoScroll={enableAutoScroll}
                                isQuickAdd={isQuickAdd}
                                themeVariant={themeVariant}
                                isAvailabilityResolved={isAvailabilityResolved}
                                isAvailabilityPending={isAvailabilityPending}
                            >
                                {/* Quick view actions */}
                                <QuickViewActions
                                    styles={styles}
                                    isNotifyMe={isNotifyMe}
                                    handleNotifyMe={handleNotifyMe}
                                    isNotifyMeLoading={isNotifyMeLoading}
                                    notifyMeError={notifyMeError}
                                    isNotifyMeModalOpen={isNotifyMeModalOpen}
                                    handleNotifyMeCloseModal={handleNotifyMeCloseModal}
                                    product={product}
                                    variant={variant}
                                    notifyMeSuccessContent={notifyMeSuccessContent}
                                    disableAddToCart={disableAddToCart}
                                    cartInventoryMessage={cartInventoryMessage}
                                    handleCartItem={cartEditMode ? cartEdit.handleUpdateCartItem : handleCartItem}
                                    shouldShowShopSimilar={shouldShowShopSimilar}
                                    onShopSimilar={handleShopSimilarOpen}
                                    cartEditMode={cartEditMode}
                                    isUpdating={cartEdit.isUpdating}
                                    updateError={cartEdit.updateError}
                                    isVariationDataLoading={isVariationDataLoading}
                                    isAvailabilityResolved={isAvailabilityResolved}
                                    isAvailabilityPending={isAvailabilityPending}
                                    notifyMeDrawerTitle={optimizelyData?.item?.NotifyMeDrawerTitle?.[0]?.ContentLink?.Expanded}
                                    notifyMeRecommendation={optimizelyData?.item?.NotifyMeRecommendation}
                                    notifyMeButtonText={notifyMeButtonText}
                                    notifyMeButtonSx={notifyMeButtonSx}
                                />
                                {isQuickViewMode && (
                                    <QuickViewFullProductLink
                                        styles={styles}
                                        product={product}
                                        variationParams={variationParams}
                                        onClick={handleFullProductLinkClick}
                                    />
                                )}
                            </QuickViewVariations>
                        </Flex>
                    )}
                </Flex>
                <QuickViewCloseButton
                    styles={styles}
                    setIsQuickViewOpen={setIsQuickViewOpen}
                    onQuickViewClose={onQuickViewClose}
                    showBackButton={showBackButton}
                    onBack={onBack}
                />
            </Box>
        )

        return (
            <>
                {disableFocusTrap || isNotifyMeModalStateOpen ? (
                    content
                ) : (
                    <FocusTrap>{content}</FocusTrap>
                )}
                {shouldShowShopSimilar && (
                    <ShopSimilarModal
                        isOpen={isShopSimilarOpen}
                        onClose={handleShopSimilarClose}
                        product={product}
                        shopSimilarDrawerTitle={optimizelyData?.item?.ShopSimilarDrawerTitle?.[0]?.ContentLink?.Expanded}
                        shopSimilarRecommendation={optimizelyData?.item?.ShopSimilarRecommendation}
                    />
                )}
            </>
        )
    }
)

QuickView.displayName = 'ProductView'

QuickView.propTypes = {
    productId: PropTypes.string,
    showImageGallery: PropTypes.bool,
    imageSize: PropTypes.oneOf(['sm', 'md']),
    isProductPartOfSet: PropTypes.bool,
    isProductPartOfBundle: PropTypes.bool,
    setIsQuickViewOpen: PropTypes.func,
    isNotifyMeModalStateOpen: PropTypes.func,
    isFavourite: PropTypes.bool,
    wishlistCount: PropTypes.number,
    setColorValue: PropTypes.func,
    setProductId: PropTypes.func,
    isWishlistPage: PropTypes.bool,
    wishlistProductId: PropTypes.string,
    disableFocusTrap: PropTypes.bool,
    onQuickViewClose: PropTypes.func,
    searchHitData: PropTypes.object,
    onVariantSelect: PropTypes.func,
    onProductChange: PropTypes.func,
    onLocalSelectionsChange: PropTypes.func,
    hasLengthVariant: PropTypes.bool,
    themeVariant: PropTypes.string,
    forceMobileLayout: PropTypes.bool,
    enableAutoScroll: PropTypes.bool,
    isQuickAdd: PropTypes.bool,
    onShopSimilarStateChange: PropTypes.func,
    showBackButton: PropTypes.bool,
    onBack: PropTypes.func,
    cartEditMode: PropTypes.bool,
    cartEditItem: PropTypes.object,
    onCartEditUpdate: PropTypes.func
}

export default QuickView
